/**
 * lib/authz/route-policy — the single source of truth for role → surface access
 * (Standard S-2.1).
 *
 * Every layout guard (/app, /admin, /superadmin, /audit) consumes `canAccess`
 * from here instead of re-declaring its own inline role Set. That kills the drift
 * the four guards had accumulated — most visibly the super_admin / superadmin
 * spelling split, where /admin accepted both spellings but /superadmin accepted
 * only the canonical one (locking a legacy-spelled super admin out of the platform
 * console). `normalizeRole` resolves that in ONE place.
 *
 * This is a UX / defence-in-depth layer: the backend is the real authorization
 * boundary (every handler re-derives tenant + role from the JWT). These checks
 * keep each surface clean and stop a wrong-role user from reaching a console they
 * can't use — they are not the security boundary on their own.
 */

/** The four gated route surfaces. */
export type Surface = 'app' | 'admin' | 'superadmin' | 'audit';

/**
 * Canonical role union — mirrors `UserRole` in `lib/api` (the JWT
 * `app_metadata.role`). The backend additionally uses `firm-reader` (a read-grant
 * act-as session) and `io`; those never reach these client guards as a top-level
 * role today, so they are intentionally absent and would fail-close to the portal
 * floor if they ever appeared (see `normalizeRole`).
 */
export type Role =
  | 'user'
  | 'admin'
  | 'rm'
  | 'compliance'
  | 'director'
  | 'super_admin'
  | 'external-auditor';

/** All canonical roles — drives the authorization matrix enumeration. */
export const CANONICAL_ROLES: readonly Role[] = [
  'user',
  'admin',
  'rm',
  'compliance',
  'director',
  'super_admin',
  'external-auditor',
];

/**
 * Normalise a raw JWT role string to a canonical `Role`.
 *   - `superadmin` (legacy, pre-snake_case migration window) → `super_admin`.
 *   - Unknown / absent (`authenticated`, `''`, a future role) → `user`, the
 *     non-privileged portal floor. Fail-closed: a role we don't recognise gets
 *     ONLY portal access, never a privileged console.
 */
export function normalizeRole(raw: string): Role {
  if (raw === 'superadmin') return 'super_admin';
  return (CANONICAL_ROLES as readonly string[]).includes(raw) ? (raw as Role) : 'user';
}

/** Firm-console roles: firm staff who work in /admin. `director` is a
 *  tenant-wide WRITER on the backend (lib/tenant-roles.ts TENANT_WIDE_WRITE_ROLES)
 *  and an invitable team role (team-invite.ts) — excluding it here stranded a
 *  provisioned director with full backend write power but no console to use it
 *  (they landed on the b2c portal and every /admin route bounced). Aligned
 *  2026-07-02 so the frontend surface matches the backend authority. */
const FIRM_STAFF: ReadonlySet<Role> = new Set<Role>(['admin', 'compliance', 'rm', 'director', 'super_admin']);

/**
 * Is `rawRole` a firm-console / tenant-wide staff role (the FIRM_STAFF set,
 * legacy `superadmin` spelling folded in via `normalizeRole`)? A `user` (b2c
 * portal / synthetic-tenant settlor) or any unrecognised role is NOT firm staff.
 *
 * Used by the /app guard to distinguish an operator who has just dropped their
 * act-as context (belongs on /admin, never on the b2c MFA step-up) from a genuine
 * portal user who legitimately owes an aal2 promotion. Kept here so the "who is
 * firm staff" set is defined exactly once, alongside `canAccess`.
 */
export function isFirmStaffRole(rawRole: string): boolean {
  return FIRM_STAFF.has(normalizeRole(rawRole));
}

/**
 * Can a caller carrying `rawRole` enter `surface`? Fail-closed via `normalizeRole`.
 */
export function canAccess(surface: Surface, rawRole: string): boolean {
  const role = normalizeRole(rawRole);
  switch (surface) {
    case 'superadmin':
      return role === 'super_admin';
    case 'admin':
      return FIRM_STAFF.has(role);
    case 'audit':
      return role === 'external-auditor';
    case 'app':
      // The client portal: every authenticated role EXCEPT the read-only external
      // auditor (bounced to /audit). Firm staff reach /app only through an act-as
      // session; a b2c settlor legitimately carries the `admin` role (they
      // administer their own synthetic tenant), so /app cannot positively exclude
      // `admin` by role alone — there is no b2c/firm signal on the client. The
      // act-as entry (Client Book / "Open client portal") + the /admin beforeLoad
      // clear are what keep firm staff out of /app except when administering.
      return role !== 'external-auditor';
  }
}

/** The canonical roles permitted on `surface`. Drives the route-authorization matrix. */
export function rolesForSurface(surface: Surface): Role[] {
  return CANONICAL_ROLES.filter((r) => canAccess(surface, r));
}

/**
 * ── THE CLIENT'S OWN FOUR SCREENS ───────────────────────────────────────────
 *
 * Four `/app` destinations belong to the CLIENT and to nobody standing in for
 * them. A firm holding an act-as token must not reach any of them.
 *
 *   /app/inbox        the client's private correspondence
 *   /app/security     their password, their authenticator, their sessions
 *   /app/firm-access  the page on which they grant and REVOKE this very firm
 *   /app/privacy      the scopes deciding what this very firm may see
 *
 * ASKED THREE TIMES. Beno, acting as the firm on 22 August, opened Firm access
 * and revoked Klaprops' own access. Paul, 25 August 14:24: *"I shouldn't be able
 * to access their inbox. And I shouldn't be able to access a firm access or
 * privacy."* Paul again, 25 August 19:31: *"I actually asked AI to remove the
 * fact that you as an administrator can access the privacy, can see security and
 * inbox. I just would want them to see the trusts and the overview."*
 *
 * That last clause is the whole specification: **the trusts and the overview**.
 *
 * WHY BY PATH AND NOT BY ROLE. The act-as token deliberately carries role
 * 'admin' so the firm can do the work it was granted. Role is therefore the
 * wrong signal; POSTURE is the right one — `firmActStatus() === 'active'` means
 * somebody is standing in for somebody else, whatever their tier says.
 *
 * WHY A GUARD AND NOT JUST A HIDDEN LINK. `screen-shell.tsx` also filters these
 * out of the sidebar, which stops the accident. This stops the typed URL, the
 * stale bookmark and the back button. The server-side privacy scopes shipped on
 * 23 August stop the fetch. Three layers, one list — this one.
 */
export const CLIENT_ONLY_PATHS: readonly string[] = [
  '/app/inbox',
  '/app/security',
  '/app/firm-access',
  '/app/privacy',
];

/**
 * Is `pathname` one of the client's own screens?
 *
 * Matches the path and everything beneath it, so a child route added later
 * (`/app/security/sessions`) is covered the day it ships rather than the day
 * somebody remembers to add it here.
 */
export function isClientOnlyPath(pathname: string): boolean {
  return CLIENT_ONLY_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
}
